import React from 'react';
import { useLocation, useNavigate, Outlet, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DashboardOutlined,
  BankOutlined,
  UserOutlined,
  TeamOutlined,
  TranslationOutlined,
  SettingOutlined,
  MessageOutlined,
  BookOutlined,
  FolderOutlined,
  TagOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import { Spin } from 'antd';
import { SuperAdminLayoutComponent } from './SuperAdminLayoutComponent';
import type { NavigationItem } from '../types';
import { SuperAdminRole } from '@municipality/types';
import { useRealTimeConversations } from '@municipality/chat';

export const SuperAdminLayout: React.FC = () => {
  const { user, loading, logOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('common');

  // Must be called unconditionally (Rules of Hooks) - before any early returns
  useRealTimeConversations(user?.uid, 'super_admin', undefined);

  // Get user's super admin role
  const superAdminRole = (user as { superAdminRole?: SuperAdminRole })?.superAdminRole;

  // Helper function to check if user can access module
  const canAccessUserManagement = 
    superAdminRole === SuperAdminRole.SUPER_ADMIN || 
    superAdminRole === SuperAdminRole.ADMINISTRATOR ||
    superAdminRole === SuperAdminRole.REGION_MANAGER;

  const canAccessMunicipalities = 
    superAdminRole === SuperAdminRole.SUPER_ADMIN || 
    superAdminRole === SuperAdminRole.ADMINISTRATOR;

  const canAccessUserRoles = superAdminRole === SuperAdminRole.SUPER_ADMIN;

  // Create navigation items with translations and role-based filtering
  const allNavItems: NavigationItem[] = [
    { 
      key: '/dashboard', 
      label: t('navigation.dashboard'), 
      icon: <DashboardOutlined />, 
      path: '/dashboard' 
    },
    { 
      key: '/chat', 
      label: t('navigation.chat'), 
      icon: <MessageOutlined />, 
      path: '/chat' 
    },
    { 
      key: '/municipalities', 
      label: t('navigation.municipalities'), 
      icon: <BankOutlined />, 
      path: '/municipalities',
      visible: canAccessMunicipalities
    },
    { 
      key: '/users', 
      label: t('navigation.users'), 
      icon: <UserOutlined />, 
      path: '/users',
      visible: canAccessUserManagement
    },
    { 
      key: '/user-roles', 
      label: t('navigation.userRoles'), 
      icon: <TeamOutlined />, 
      path: '/user-roles',
      visible: canAccessUserRoles
    },
    { 
      key: '/translations', 
      label: t('navigation.translations'), 
      icon: <TranslationOutlined />, 
      path: '/translations' 
    },
    {
      key: 'knowledge-base',
      label: t('navigation.knowledgeBase'),
      icon: <BookOutlined />,
      children: [
        {
          key: '/knowledge-base',
          label: t('knowledgeBase:dashboard.title'),
          icon: <BookOutlined />,
          path: '/knowledge-base',
        },
        {
          key: '/knowledge-base/categories',
          label: t('knowledgeBase:breadcrumbs.categories'),
          icon: <FolderOutlined />,
          path: '/knowledge-base/categories',
        },
        {
          key: '/knowledge-base/tags',
          label: t('knowledgeBase:breadcrumbs.tags'),
          icon: <TagOutlined />,
          path: '/knowledge-base/tags',
        },
        {
          key: '/knowledge-base/articles',
          label: t('knowledgeBase:breadcrumbs.articles'),
          icon: <FileTextOutlined />,
          path: '/knowledge-base/articles',
        },
      ],
    },
    { 
      key: '/settings', 
      label: t('navigation.settings'), 
      icon: <SettingOutlined />, 
      path: '/settings' 
    },
  ];

  // Filter navigation items based on visibility
  const navItems = allNavItems.filter(item => {
    if ('visible' in item) {
      return item.visible !== false;
    }
    return true;
  });

  const getBreadcrumbItems = (pathname: string) => {
    const pathSnippets = pathname.split('/').filter((i) => i);
    const items = [{ 
      title: t('navigation.dashboard'), 
      path: pathname === '/dashboard' ? undefined : '/dashboard' 
    }];
    pathSnippets.forEach((_, index) => {
      const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
      const navItem = navItems.find(item => 
        !('type' in item && item.type === 'divider') && 
        (item as { path?: string }).path === url
      );
      if (navItem && !('type' in navItem)) {
        const item = navItem as { path?: string; label: string };
        if (item.path && item.path !== url) {
          items.push({ title: item.label, path: item.path });
        }
      }
    });
    return items;
  };

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const breadcrumbItems = getBreadcrumbItems(location.pathname);

  // Map user data to the format expected by layout component
  const layoutUser = user ? {
    displayName: user.displayName || user.email?.split('@')[0] || 'Super Admin',
    email: user.email || '',
    name: (user as { name?: string }).name,
    surname: (user as { surname?: string }).surname,
  } : undefined;

  return (
    <SuperAdminLayoutComponent
      user={layoutUser}
      navItems={navItems}
      breadcrumbItems={breadcrumbItems}
      onLogout={logOut}
      onNavigate={handleNavigate}
      onLanguageChange={handleLanguageChange}
      showLanguageSwitcher={true}
    >
      <Outlet />
    </SuperAdminLayoutComponent>
  );
}; 